import { FC } from "react";
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors,
} from "react-hook-form";
import InputField from "app/components/common/inputs/InputField";
import FormGroup from "app/components/common/inputs/FormGroup";
import AutocompleteLocations from "app/components/common/AutocompleteLocations";
import SelectField from "app/components/common/inputs/SelectField";
import {
  CurrentemploymentOptions,
  stateOfIssueOptions,
} from "app/utils/staticValues";

interface Props {
  control: Control<any>;
  errors: any;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  currentEmployment: string;
  currentOccupation: string;
  employerName: string;
  employerPhoneNumber: string;
  addressStreetName: string;
  addressStateName: string;
  addressPostCodeName: string;
  abn: string;
  currentEmploymentType?: string;
}

const DynamicFormOne: FC<Props> = ({
  control,
  errors,
  register,
  setValue,
  setError,
  clearErrors,
  currentEmployment,
  currentOccupation,
  employerName,
  employerPhoneNumber,
  addressStreetName,
  addressStateName,
  addressPostCodeName,
  abn,
  currentEmploymentType,
}) => {
  const getErrorMessage = (fieldName: string) => {
    const fieldNames = fieldName.split(".");
    return fieldNames.reduce((acc, key) => acc && acc[key], errors);
  };

  const isEmployerRequired = currentEmploymentType === "unemployed_or_retired";
  const isNotRequired =
    currentEmploymentType === "contractor" ||
    currentEmploymentType === "self-employed" ||
    currentEmploymentType === "unemployed_or_retired";

  return (
    <>
      <FormGroup>
        <SelectField
          label="Current employment"
          name={currentEmployment}
          control={control}
          options={CurrentemploymentOptions}
          placeholder="Please select employment"
          error={getErrorMessage(currentEmployment)?.message as string}
          required={true}
          className="flex-1"
        />
        <InputField
          label="Current occupation"
          name={currentOccupation}
          control={control}
          type="text"
          required={!isNotRequired}
          placeholder="occupation"
          error={getErrorMessage(currentOccupation)?.message as string}
          className="flex-1"
          rules={{
            // required: "Occupation is required",
            pattern: {
              value: /^[A-Za-z\s]+$/,
              message: "Only alphabets are allowed",
            },
          }}
          disabled={isEmployerRequired}
        />
      </FormGroup>
      <FormGroup>
        <InputField
          label="Employer name"
          name={employerName}
          control={control}
          type="text"
          required={!isNotRequired}
          placeholder="Designer"
          error={getErrorMessage(employerName)?.message as string}
          className="flex-1"
          rules={{
            // required: "Employer name is required",
            pattern: {
              value: /^[A-Za-z\s]+$/,
              message: "Only alphabets are allowed",
            },
          }}
          disabled={isEmployerRequired}
        />
        <InputField
          label="Employer phone number"
          name={employerPhoneNumber}
          control={control}
          type="number"
          required={!isNotRequired}
          placeholder="(201) 555-0124"
          error={getErrorMessage(employerPhoneNumber)?.message as string}
          className="flex-1"
          rules={{
            min: {
              value: 1,
              message: "value must be greater than 0",
            },
          }}
          disabled={isEmployerRequired}
        />
      </FormGroup>
      <FormGroup>
        <AutocompleteLocations
          FieldName={addressStreetName}
          placeholder="1901 Thornridge Cir. Shiloh,"
          register={register}
          setValue={setValue}
          setError={setError}
          label="Employer Address"
          required={!isNotRequired}
          className="flex-1"
          error={getErrorMessage(addressStreetName)?.message as string}
          stateFieldName={addressStateName}
          postCodeFieldName={addressPostCodeName}
          clearErrors={clearErrors}
          disabled={isEmployerRequired}
        />
        <SelectField
          label="State"
          name={addressStateName}
          control={control}
          options={stateOfIssueOptions}
          placeholder="state"
          error={getErrorMessage(addressStateName)?.message as string}
          required={!isNotRequired}
          className="flex-1"
          isDisabled={isEmployerRequired}
        />
      </FormGroup>
      <FormGroup>
        <InputField
          label="Post Code"
          name={addressPostCodeName}
          control={control}
          type="number"
          required={!isNotRequired}
          placeholder="8767"
          error={getErrorMessage(addressPostCodeName)?.message as string}
          className="flex-1"
          rules={{
            min: {
              value: 1,
              message: "value must be greater than 0",
            },
          }}
          disabled={isEmployerRequired}
        />
        <InputField
          label="ABN"
          name={abn}
          control={control}
          type="text"
          required={false}
          placeholder="12 345 678 901"
          error={getErrorMessage(abn)?.message as string}
          className="flex-1"
          rules={{
            pattern: {
              value: /^[1-9]\d{10}$/,
              message: "ABN must be 11 digits and cannot start with 0",
            },
          }}
          disabled={isEmployerRequired}
        />
      </FormGroup>
    </>
  );
};

export default DynamicFormOne;
